import { Square, PieceType, DifficultyLevel } from '../core/types.ts';

export interface AIMoveResult {
  from: Square;
  to: Square;
  promotion?: PieceType;
  score: number;
  depth: number;
  nodes: number;
}

export class ChessAI {
  private worker: Worker | null = null;
  private requestId = 0;
  private pendingRequests: Map<number, { resolve: (res: AIMoveResult) => void; reject: (err: Error) => void }> = new Map();
  private isBusy = false;

  constructor() {
    this.initWorker();
  }

  private initWorker(): void {
    try {
      this.worker = new Worker(new URL('./engineWorker.ts', import.meta.url), {
        type: 'module'
      });

      this.worker.onmessage = (event: MessageEvent) => {
        const { id, type, success, result, error } = event.data;
        const pending = this.pendingRequests.get(id);
        if (!pending) return;

        this.pendingRequests.delete(id);
        this.isBusy = false;

        if (type === 'RESULT' && success && result) {
          pending.resolve(result);
        } else {
          pending.reject(new Error(error || 'AI calculation failed'));
        }
      };

      this.worker.onerror = (err) => {
        console.error('AI Worker error:', err);
        this.isBusy = false;
        // reject all pending
        for (const [id, req] of this.pendingRequests.entries()) {
          req.reject(new Error('Worker error'));
          this.pendingRequests.delete(id);
        }
      };
    } catch (e) {
      console.warn('Worker initialization failed:', e);
      this.worker = null;
    }
  }

  public get calculating(): boolean {
    return this.isBusy;
  }

  public async getBestMove(fen: string, difficulty: DifficultyLevel): Promise<AIMoveResult> {
    if (!this.worker) {
      this.initWorker();
      if (!this.worker) {
        throw new Error('AI Engine Web Worker is not available');
      }
    }

    this.isBusy = true;
    const id = ++this.requestId;

    return new Promise((resolve, reject) => {
      this.pendingRequests.set(id, { resolve, reject });
      this.worker!.postMessage({
        id,
        type: 'SEARCH',
        fen,
        difficulty
      });
    });
  }

  public terminate(): void {
    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
      this.isBusy = false;
      this.pendingRequests.clear();
    }
  }
}
